import React, { useCallback } from 'react'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import {
  MenuItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  useTheme,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { HHFormTextField } from 'components/form-fields/v5'
import { useFormContext } from 'react-hook-form'
import T from 'T'
import { extractNameInitials } from 'utils/string'
import { isValidHex } from 'utils/validations'
import { grey } from '@mui/material/colors'
import { isStopMatch } from '../utils'

const NearestStopsDropdown = ({
  stops = [],
  onSelect,
  isLoading,
  onCollapseFilters = noop,
  filtersExpanded,
  MenuProps: MenuPropsProp,
  clearable = false,
  onClear = noop,
}) => {
  const theme = useTheme()
  const { control, watch, setValue } = useFormContext()
  const [open, setOpen] = React.useState(false)
  const selectedValue = watch('selectedNearestStop')
  const showClear = clearable && !!selectedValue

  const handleClear = React.useCallback(
    e => {
      e.stopPropagation()
      setValue('selectedNearestStop', '')
      onClear()
    },
    [setValue, onClear]
  )

  const handleValueRender = useCallback(
    selected => {
      if (!selected) return <Typography color="textSecondary">{T.SEQUENCE_WITH_NEAREST_STOP}</Typography>
      const stop = stops.find(s => isStopMatch(s, selected))
      if (!stop) return selected

      const routeName = stop.route?.name || ''
      const stopName = stop.locationName || stop.addressLine1 || ''
      return `${routeName} - ${stopName}`
    },
    [stops]
  )

  return (
    <Box mt={1}>
      <HHFormTextField
        control={control}
        name="selectedNearestStop"
        select
        fullWidth
        label={T.SEQUENCE_WITH_NEAREST_STOP}
        SelectProps={{
          open,
          onOpen: () => {
            if (filtersExpanded) {
              onCollapseFilters()
              setTimeout(() => setOpen(true), 350)
            } else {
              setOpen(true)
            }
          },
          onClose: () => setOpen(false),
          MenuProps: {
            PaperProps: {
              sx: { maxHeight: 300 },
            },
            ...MenuPropsProp,
          },
          renderValue: handleValueRender,
        }}
        InputProps={{
          startAdornment: isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null,
          endAdornment: showClear ? (
            <InputAdornment position="end" sx={{ mr: 2 }}>
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        deprecatedLabel={false}
      >
        {stops.map(stop => {
          const route = stop.route || {}
          const routeName = route.name || T.UNKNOWN_ROUTE
          const routeColor = isValidHex(route.color) ? route.color : grey[500]
          const contrastText = theme.palette.getContrastText(routeColor)
          const initials = extractNameInitials(routeName)

          const stopName = stop.locationName || stop.addressLine1 || T.UNKNOWN_LOCATION
          // Convert meters to miles: 1 meter = 0.000621371 miles
          const distanceMiles = stop.distance !== undefined ? `${(stop.distance * 0.000621371).toFixed(2)} mi` : ''

          return (
            <MenuItem
              value={stop.id || stop.workOrderId}
              key={stop.id || stop.workOrderId}
              onClick={() => onSelect(stop)} // Manually trigger onSelect since we are handling value via form
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: routeColor,
                    color: contrastText,
                    fontSize: '0.75rem',
                  }}
                >
                  {initials}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight="medium">
                      {stopName}
                    </Typography>
                    {distanceMiles && (
                      <Typography variant="caption" color="textSecondary">
                        {distanceMiles}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="textSecondary" noWrap display="block">
                    {routeName}
                  </Typography>
                }
              />
            </MenuItem>
          )
        })}
        {!isLoading && stops.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={T.NO_OPTIONS} secondary={T.TRY_CHANGING_FILTERS} />
          </MenuItem>
        )}
      </HHFormTextField>
    </Box>
  )
}

NearestStopsDropdown.propTypes = {
  stops: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onCollapseFilters: PropTypes.func,
  filtersExpanded: PropTypes.bool,
  MenuProps: PropTypes.object,
  clearable: PropTypes.bool,
  onClear: PropTypes.func,
}

export default NearestStopsDropdown
